import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid, TextField, Button } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import BasicDialog from '../../base/BasicDialog';
import { useSnackbarAlert } from '../../../context/snackbarAlert';
import { useForm } from '../../../hooks/useForm';
import { useAsync, useAsyncFn } from '../../../hooks/useAsync';
import { isEmptyValue } from '../../../helpers/methods';
import TextFieldFormControl from '../../field/TextFieldFormControl';
import CheckboxField from '../../base/CheckboxField/checkboxField';
import useCorrespondenceAdminService from '../../../services/correspondenceAdminService';

const CreatePostSideTypeDialog = (props) => {
  const { t } = useTranslation();
  const { formValue, setFormValue, onChange } = useForm({ side_type_active: true });

  const { createPostSideType, updatePostSideTypeData, getPostSideTypeDetails } = useCorrespondenceAdminService();
  const createPostSideTypeFn = useAsyncFn(createPostSideType);
  const updatePostSideTypeFn = useAsyncFn(updatePostSideTypeData);
  const snackbarAlert = useSnackbarAlert();

  const postSideTypeDetailsData = useAsync(() => {
    if (props.sideTypeId) {
      return getPostSideTypeDetails(props.sideTypeId);
    }
    return Promise.resolve({});
  }, [props.sideTypeId]);

  useEffect(() => {
    if (!postSideTypeDetailsData.loading && postSideTypeDetailsData.value && props.sideTypeId) {
      setFormValue(postSideTypeDetailsData.value);
    }
  }, [props.sideTypeId, postSideTypeDetailsData.loading]);

  const onChangeCheckbox = useCallback((e) => {
    const { name, checked } = e.target;
    setFormValue((prev) => ({ ...prev, [name]: checked }));
  }, []);

  const onSubmit = (postSideTypeData) => {
    if (postSideTypeData.id) {
      updatePostSideTypeFn.execute(postSideTypeData.id, postSideTypeData)
        .then(() => {
          snackbarAlert.openSuccessSnackbarAlert(t("snackbar_alert.post_side_type_updated"));
          if (props.onRefetchData) {
            props.onRefetchData();
          }
          props.onClose(true);
        })
        .catch(() => {
          snackbarAlert.openErrorSnackbarAlert(t("snackbar_alert.occurred_error_during_updating_post_side_type"));
        });
    } else {
      createPostSideTypeFn.execute(postSideTypeData)
        .then(() => {
          snackbarAlert.openSuccessSnackbarAlert(t("snackbar_alert.post_side_type_created"));
          if (props.onRefetchData) {
            props.onRefetchData();
          }
          props.onClose(true);
        })
        .catch(() => {
          snackbarAlert.openErrorSnackbarAlert(t("snackbar_alert.occurred_error_during_creating_new_post_side_type"));
        });
    }
  };

  const handleSubmit = () => {
    if (isFormValid()) {
      onSubmit(formValue);
    }
  };

  const isFormValid = () => {
    return !isEmptyValue(formValue.side_type_name);
  };

  return (
    <BasicDialog
      open={props.open}
      onClose={props.onClose}
      titleAlign="center"
      maxWidth="md"
      title={props.sideTypeId ? t('dialog.correspondence_admin_panel_dialogs.edit_side_type') : t('dialog.correspondence_admin_panel_dialogs.add_side_type')}
    >
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <TextFieldFormControl
            name="side_type_name"
            label={t('dialog.correspondence_admin_panel_dialogs.side_type_name')}
            value={formValue["side_type_name"]}
            onChange={onChange}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={4}>
          <CheckboxField
            label={t('dialog.correspondence_admin_panel_dialogs.side_type_input_administration')}
            name="side_type_input_administration"
            checked={formValue["side_type_input_administration"]}
            onChange={onChangeCheckbox}
          />
        </Grid>
        <Grid item xs={4}>
          <CheckboxField
            label={t('dialog.correspondence_admin_panel_dialogs.active')}
            name="side_type_active"
            checked={formValue["side_type_active"]}
            onChange={onChangeCheckbox}
          />
        </Grid>
        <Grid item xs={6}>
          <LoadingButton
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleSubmit}
            disabled={!isFormValid()}
          >
            {props.sideTypeId ? t('save') : t('add')}
          </LoadingButton>
        </Grid>
        <Grid item xs={6}>
          <Button
            variant="outlined"
            color="secondary"
            fullWidth
            onClick={props.onClose}
          >
            {t('cancel')}
          </Button>
        </Grid>
      </Grid>
    </BasicDialog>
  );
};

CreatePostSideTypeDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  sideTypeId: PropTypes.string,
  onRefetchData: PropTypes.func.isRequired
};

CreatePostSideTypeDialog.defaultProps = {
  sideTypeId: null
};

export default CreatePostSideTypeDialog;
